import { observer } from "mobx-react-lite";

import { IOverflowSetItemProps, Spinner } from "@bps/fluent-ui";
import { DocumentTabStatus } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { catchNotFoundError } from "@libs/utils/utils.ts";
import { ClinicalDocument } from "@stores/clinical/models/ClinicalDocument.ts";
import { Template } from "@stores/documents/models/Template.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { ErrorAlert } from "@ui-components/alert/Alert.tsx";
import { ContactFetcher } from "@ui-components/ContactFetcher.tsx";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { DocumentPill } from "./DocumentPill.tsx";
import { PatientPill } from "./PatientPill.tsx";
import { TemplatePill } from "./TemplatePill.tsx";

interface QuickLinkProps {
  item: IOverflowSetItemProps;
  handleDocumentClick: (
    id: string,
    patientId: string,
    encounterId: string
  ) => void;
  handleTemplateClick: (documentId: string) => void;
}

const renderStyledErrorAlert = (error: Error) => (
  <ErrorAlert
    error={error}
    styles={{ root: { height: 32, width: 200, button: { height: 24 } } }}
  />
);

export const QuickLink = observer(
  ({ item, handleDocumentClick, handleTemplateClick }: QuickLinkProps) => {
    const { correspondence, document } = useStores();

    if (item.type === "patient") {
      return (
        <ContactFetcher
          contactId={item.patientId}
          fallback={<Spinner />}
          renderError={renderStyledErrorAlert}
        >
          {patient => (
            <PatientPill
              active={item.active}
              record={item.record}
              patient={patient}
              encounterId={item.encounterId}
            />
          )}
        </ContactFetcher>
      );
    }

    if (item.type === "document") {
      return (
        <DataFetcher<(ClinicalDocument | undefined) | undefined>
          fallback={<Spinner />}
          fetch={async ({ correspondence }) => {
            if (
              (item.documentTabStatus === DocumentTabStatus.New ||
                !item.documentTabStatus) &&
              !correspondence.correspondenceMap.get(item.key)
            ) {
              // If document is new and not in correspondence map the screen has been refreshed and we cannot show the document so remove from the openDocumentTabs array
              await correspondence.closeDocument(item.key);
              return undefined;
            }

            return correspondence.getCorrespondenceByDocumentId(
              item.patientId,
              item.key
            );
          }}
        >
          {doc => {
            if (doc) {
              return (
                <ContactFetcher
                  contactId={doc.patientId}
                  fallback={<Spinner />}
                  renderError={renderStyledErrorAlert}
                >
                  {patient => (
                    <DocumentPill
                      onClose={async () =>
                        await correspondence.closeDocument(item.key)
                      }
                      onClick={() =>
                        handleDocumentClick(
                          item.key,
                          item.patientId,
                          item.encounterId
                        )
                      }
                      active={item.active}
                      clinDocument={doc}
                      patient={patient}
                    />
                  )}
                </ContactFetcher>
              );
            }
            return undefined;
          }}
        </DataFetcher>
      );
    }

    if (item.type === "template") {
      return (
        <DataFetcher<Template | undefined>
          fetch={async ({ document }) => {
            let key: string = item.key;
            if (
              item.documentTabStatus === DocumentTabStatus.New &&
              !document.templateMap.get(item.key)
            ) {
              // If template is new and not in template map the screen has been refreshed and we cannot show the template so remove from the openDocumentTabs array
              await document.closeTemplate(item.key);
              return undefined;
            }
            if (
              item.documentTabStatus === DocumentTabStatus.Edit &&
              item.newDocumentId
            ) {
              document.updateTemplateTab(item.key, item.newDocumentId, false);
              key = item.newDocumentId;
            }

            return document
              .getTemplateByDocumentId(key)
              .catch(catchNotFoundError);
          }}
        >
          {(temp: Template) => {
            if (temp) {
              return (
                <TemplatePill
                  onClose={() => document.closeTemplate(temp.id)}
                  onClick={() => handleTemplateClick(temp.id)}
                  active={item.active}
                  template={temp}
                />
              );
            }
            return undefined;
          }}
        </DataFetcher>
      );
    }

    return null;
  }
);
